import React from 'react';
import { useStore } from '../../store/StoreContext';
import { calculateSchedule } from '../../utils/helpers';
import SafeIcon from '../../common/SafeIcon';

export const Step4Review = ({ projectData, workflows }) => {
  const { state } = useStore();
  const scheduled = calculateSchedule({ ...projectData, workflows }, state.categories);
  const bu = state.businessUnits[projectData.businessUnitId];

  const variance = scheduled.deadlineVariance;
  const isOverdue = variance < 0;
  const varianceAbs = Math.abs(variance);

  return (
    <div className="h-full flex flex-col items-center justify-center max-w-4xl mx-auto py-8 overflow-y-auto shrink-0">
      <div className="w-full bg-base-800 rounded-2xl border border-base-700 overflow-hidden shadow-2xl">
        <div className="p-8 border-b border-base-700 bg-base-900/50 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: projectData.color }}></div>
              <h2 className="text-2xl font-bold">{projectData.name || 'Untitled Project'}</h2>
            </div>
            <p className="text-gray-400 max-w-lg">{projectData.description || 'No description provided.'}</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500 uppercase font-bold mb-1">Business Unit</div>
            <div className="text-primary font-semibold">{bu?.name || 'N/A'}</div>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-6">
            <div>
              <div className="text-xs text-gray-500 uppercase font-bold mb-2">Timeline</div>
              <div className="flex items-center gap-2 text-sm">
                <SafeIcon name="Calendar" className="text-accent w-4 h-4" />
                <span className="font-mono">{projectData.startDate}</span>
                <SafeIcon name="ArrowRight" className="text-gray-600 w-3 h-3" />
                <span className="font-mono">{scheduled.calculatedEndDate}</span>
              </div>
            </div>

            {projectData.deadline && (
              <div className="p-4 rounded-xl bg-base-900 border border-base-700">
                <div className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-2">Deadline Health</div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold text-white font-mono">{projectData.deadline}</div>
                  <div className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase ${isOverdue ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'}`}>
                    {isOverdue ? 'At Risk' : 'On Track'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOverdue ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}>
                    <SafeIcon name={isOverdue ? "AlertTriangle" : "CheckCircle"} className="w-5 h-5" />
                  </div>
                  <div>
                    <div className={`text-lg font-black leading-tight ${isOverdue ? 'text-red-400' : 'text-green-400'}`}>
                      {varianceAbs} {varianceAbs === 1 ? 'Day' : 'Days'}
                    </div>
                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter">
                      {isOverdue ? 'After Deadline' : 'Before Deadline'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs text-gray-500 uppercase font-bold mb-2">Total Effort</div>
              <div className="text-3xl font-mono font-bold text-accent">{scheduled.totalHours}h</div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs text-gray-500 uppercase font-bold mb-3">Workflow Phases</div>
            <div className="space-y-2">
              {scheduled.scheduledWorkflows.map((wf, i) => (
                <div key={i} className="bg-base-900 p-3 rounded-lg border border-base-700 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono bg-base-700 px-1.5 py-0.5 rounded text-gray-400">P{i + 1}</span>
                    <span className="text-sm font-medium">{wf.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">{wf.totalHours}h | {wf.tasks.length} tasks</div>
                    <div className="text-[9px] text-gray-600 font-mono">{wf.startDate} to {wf.endDate}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};